/**
 * Remembers, for this browser tab only, which addresses Continue resolved to a
 * SAML tenant that could not start the identity provider.
 *
 * That is the 400 with password_login: false. The page stays here, and typing
 * the same address again should still offer "IdP not working?" without another
 * round trip. A redirect to the IdP is not stored: that navigation leaves this
 * page, and there is no way back onto it, so a remembered address would keep
 * the link up with nothing to dismiss it.
 *
 * What is stored is the sign-in method of an address the visitor typed
 * themselves, which login_sso already tells any caller that asks. It never
 * records who the tenant's emergency account is.
 *
 * sessionStorage, not localStorage: this is a hint for one sign-in, not a
 * profile to keep on a shared machine.
 */

const STORAGE_KEY = "unixi-saml-accounts";

// An admin may try a couple of addresses before the right one. Beyond a handful
// this is stale guesswork, so the list stays small and most-recent-first.
const MAX_REMEMBERED = 5;

/**
 * Match the backend exactly: login_sso lower-cases and strips before it looks
 * an address up, so "Admin@Acme.com " and "admin@acme.com" are one account and
 * must share one entry here.
 */
function canonicalize(email) {
    return typeof email === "string" ? email.trim().toLowerCase() : "";
}

/**
 * Storage can throw or come back empty - private windows, blocked site data, an
 * embedded frame. Every path here degrades to "we remember nothing", which only
 * costs the visitor the pre-Continue shortcut.
 */
function read() {
    try {
        const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
        return Array.isArray(stored) ? stored.filter((entry) => typeof entry === "string") : [];
    } catch (error) {
        return [];
    }
}

function write(entries) {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(0, MAX_REMEMBERED)));
    } catch (error) {
        // Nothing to recover: the link simply waits for the next Continue.
    }
}

/** Record that this address signs in through an identity provider. */
export function rememberSamlAccount(email) {
    const account = canonicalize(email);
    if (!account) return;

    write([account, ...read().filter((entry) => entry !== account)]);
}

/**
 * Drop an address, used when Continue comes back with password_login: the
 * tenant moved off SAML, or the earlier guess was simply wrong. Leaving a stale
 * entry would keep offering emergency access to an account that has no IdP to
 * fail.
 */
export function forgetSamlAccount(email) {
    const account = canonicalize(email);
    if (!account) return;

    const remaining = read().filter((entry) => entry !== account);
    write(remaining);
}

/** Has Continue already resolved this address to an identity provider that could not start? */
export function isKnownSamlAccount(email) {
    const account = canonicalize(email);
    return !!account && read().includes(account);
}
